import { useEffect, useMemo, useCallback, useState, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';

import { Capture, CaptureStatus } from '@/types';
import { firebaseUserAtom } from '@/stores/auth';
import { queryKeys } from '@/lib/queryKeys';
import {
  subscribeToCaptures,
  createCapture as repoCreateCapture,
  updateCapture as repoUpdateCapture,
  deleteCapture as repoDeleteCapture,
  processCapture as repoProcessCapture,
  archiveCapture as repoArchiveCapture,
  CreateCaptureInput,
  UpdateCaptureInput,
} from '@/repositories/captures.repository';
import {
  filterCapturesByStatus,
  getInboxCaptures,
  getProcessedCaptures,
  getArchivedCaptures,
  getInboxCount,
} from '@/services/captures.service';

// ============================================
// Types
// ============================================

export interface UseCapturesOptions {
  statusFilter?: CaptureStatus | 'all';
}

export interface ProcessCaptureOptions {
  convertTo?: 'task' | 'block';
  convertedId?: string;
}

export interface UseCapturesResult {
  captures: Capture[];
  inboxCaptures: Capture[];
  processedCaptures: Capture[];
  archivedCaptures: Capture[];
  isLoading: boolean;
  error: Error | null;
  inboxCount: number;
  createCapture: (input: CreateCaptureInput) => Promise<Capture | null>;
  updateCapture: (id: string, updates: UpdateCaptureInput) => Promise<boolean>;
  deleteCapture: (id: string) => Promise<boolean>;
  processCapture: (id: string, options?: ProcessCaptureOptions) => Promise<boolean>;
  archiveCapture: (id: string) => Promise<boolean>;
}

// Re-export input types for consumers
export type { CreateCaptureInput, UpdateCaptureInput };

// ============================================
// Hook
// ============================================

export function useCaptures(options: UseCapturesOptions = {}): UseCapturesResult {
  const firebaseUser = useAtomValue(firebaseUserAtom);
  const queryClient = useQueryClient();

  const [isListenerInitialized, setIsListenerInitialized] = useState(false);
  const prevUserIdRef = useRef<string | null>(null);

  const userId = firebaseUser?.uid;
  const { statusFilter = 'all' } = options;
  const queryKey = queryKeys.captures.byUser(userId);

  // Use React Query for caching and state management
  const {
    data: captures = [],
    isLoading: isQueryLoading,
    error,
  } = useQuery<Capture[], Error>({
    queryKey,
    queryFn: async () => [],
    enabled: !!userId,
    staleTime: Infinity,
  });

  // Set up real-time Firestore listener
  useEffect(() => {
    if (prevUserIdRef.current !== userId) {
      prevUserIdRef.current = userId || null;
      setIsListenerInitialized(false);
    }

    if (!userId) {
      setIsListenerInitialized(true);
      return;
    }

    let isCleanedUp = false;

    const timeoutId = setTimeout(() => {
      if (!isCleanedUp && !isListenerInitialized) {
        console.warn('Firestore captures listener timeout - marking as initialized');
        setIsListenerInitialized(true);
      }
    }, 2000);

    const unsubscribe = subscribeToCaptures(
      userId,
      (capturesData) => {
        if (isCleanedUp) return;
        queryClient.setQueryData(queryKey, capturesData);
        setIsListenerInitialized(true);
      },
      () => {
        if (isCleanedUp) return;
        setIsListenerInitialized(true);
      }
    );

    return () => {
      isCleanedUp = true;
      clearTimeout(timeoutId);
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Derived data using service layer
  const filteredCaptures = useMemo(
    () => filterCapturesByStatus(captures, statusFilter),
    [captures, statusFilter]
  );
  const inboxCaptures = useMemo(() => getInboxCaptures(captures), [captures]);
  const processedCaptures = useMemo(() => getProcessedCaptures(captures), [captures]);
  const archivedCaptures = useMemo(() => getArchivedCaptures(captures), [captures]);
  const inboxCount = useMemo(() => getInboxCount(captures), [captures]);

  // CRUD operations using repository
  const createCapture = useCallback(
    async (input: CreateCaptureInput): Promise<Capture | null> => {
      if (!userId) return null;
      try {
        return await repoCreateCapture(userId, input);
      } catch (error) {
        console.error('Error creating capture:', error);
        return null;
      }
    },
    [userId]
  );

  const updateCapture = useCallback(
    async (id: string, updates: UpdateCaptureInput): Promise<boolean> => {
      if (!userId) return false;
      try {
        await repoUpdateCapture(id, updates);
        return true;
      } catch (error) {
        console.error('Error updating capture:', error);
        return false;
      }
    },
    [userId]
  );

  const deleteCapture = useCallback(
    async (id: string): Promise<boolean> => {
      if (!userId) return false;
      try {
        await repoDeleteCapture(id);
        return true;
      } catch (error) {
        console.error('Error deleting capture:', error);
        return false;
      }
    },
    [userId]
  );

  const processCapture = useCallback(
    async (id: string, options: ProcessCaptureOptions = {}): Promise<boolean> => {
      if (!userId) return false;
      try {
        await repoProcessCapture(id, options.convertTo, options.convertedId);
        return true;
      } catch (error) {
        console.error('Error processing capture:', error);
        return false;
      }
    },
    [userId]
  );

  const archiveCapture = useCallback(
    async (id: string): Promise<boolean> => {
      if (!userId) return false;
      try {
        await repoArchiveCapture(id);
        return true;
      } catch (error) {
        console.error('Error archiving capture:', error);
        return false;
      }
    },
    [userId]
  );

  // Loading state
  const hasCaptures = captures.length > 0;
  const isLoading = !!userId && !isListenerInitialized && !hasCaptures;

  return {
    captures: filteredCaptures,
    inboxCaptures,
    processedCaptures,
    archivedCaptures,
    isLoading,
    error: error ?? null,
    inboxCount,
    createCapture,
    updateCapture,
    deleteCapture,
    processCapture,
    archiveCapture,
  };
}
